import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell,
  Package,
  Wallet,
  MessageSquare,
  CheckCheck,
  Loader2,
} from 'lucide-react';

const CATEGORY_LABELS = {
  operations: 'Operations',
  finance: 'Finance',
  contacts: 'Contacts',
};

const CATEGORY_ORDER = ['operations', 'finance', 'contacts'];

function relativeTime(date) {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function CategoryIcon({ category }) {
  if (category === 'finance') return <Wallet className="w-4 h-4" />;
  if (category === 'contacts') return <MessageSquare className="w-4 h-4" />;
  return <Package className="w-4 h-4" />;
}

export default function AdminNotificationPanel({
  apiBase,
  authToken,
  onNavigate,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);

  const headers = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken],
  );

  const fetchUnreadCount = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${apiBase}/api/notifications/unread-count`, { headers });
      const json = await res.json().catch(() => ({}));
      if (res.ok) setUnreadCount(Number(json.count) || 0);
    } catch {
      /* ignore poll errors */
    }
  }, [apiBase, authToken, headers]);

  const fetchNotifications = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/notifications?limit=30`, { headers });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setItems(Array.isArray(json.data) ? json.data : []);
        setUnreadCount((json.data || []).filter((i) => !i.isRead).length);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken, headers]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        panelRef.current?.contains(e.target)
        || bellRef.current?.contains(e.target)
      ) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const markRead = async (id) => {
    if (!authToken || !id) return;
    try {
      await fetch(`${apiBase}/api/notifications/${encodeURIComponent(id)}/read`, {
        method: 'PATCH',
        headers,
      });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, isRead: true } : i)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    if (!authToken) return;
    try {
      await fetch(`${apiBase}/api/notifications/read-all`, { method: 'PATCH', headers });
      setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
      setUnreadCount(0);
    } catch {
      /* ignore */
    }
  };

  const handleItemClick = async (item) => {
    if (!item.isRead) await markRead(item.id);
    if (item.actionTab) onNavigate?.(item.actionTab);
    setOpen(false);
  };

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const cat = item.category || 'operations';
      if (!map[cat]) map[cat] = [];
      map[cat].push(item);
    });
    return CATEGORY_ORDER.filter((c) => map[c]?.length).map((c) => ({ category: c, items: map[c] }));
  }, [items]);

  return (
    <div className="jet-notif-wrap">
      <button
        ref={bellRef}
        type="button"
        className={`jet-notif${open ? ' jet-notif--open' : ''}`}
        aria-label="Notifications"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 ? (
          <span className="jet-notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        ) : null}
      </button>

      {open ? (
        <div ref={panelRef} className="jet-notif-panel" role="dialog" aria-label="Notifications">
          <div className="jet-notif-panel-head">
            <strong>Notifications</strong>
            {unreadCount > 0 ? (
              <button type="button" className="jet-notif-mark-all" onClick={markAllRead}>
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            ) : null}
          </div>

          <div className="jet-notif-panel-body">
            {loading ? (
              <div className="jet-notif-loading">
                <Loader2 className="w-5 h-5 jet-notif-spin" />
                Loading…
              </div>
            ) : items.length === 0 ? (
              <p className="jet-notif-empty">No notifications right now.</p>
            ) : (
              grouped.map(({ category, items: catItems }) => (
                <div key={category} className="jet-notif-group">
                  <div className="jet-notif-group-title">{CATEGORY_LABELS[category] || category}</div>
                  {catItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`jet-notif-item${item.isRead ? '' : ' jet-notif-item--unread'}`}
                      onClick={() => handleItemClick(item)}
                    >
                      <span className={`jet-notif-item-icon jet-notif-item-icon--${item.type || 'info'}`}>
                        <CategoryIcon category={item.category} />
                      </span>
                      <span className="jet-notif-item-body">
                        <span className="jet-notif-item-title">{item.title}</span>
                        <span className="jet-notif-item-msg">{item.message}</span>
                        <span className="jet-notif-item-time">{relativeTime(item.createdAt)}</span>
                      </span>
                      {!item.isRead ? <span className="jet-notif-unread-dot" aria-hidden /> : null}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
